import { driver, type DriveStep, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './guideDashboardTour.css';
import { STORAGE_KEYS } from '@/utils/constants';

export const GUIDE_TOUR_ATTR = 'data-guide-tour';

export type GuideTourSlug =
  | 'today'
  | 'bookings'
  | 'past-trips'
  | 'pricing'
  | 'calendar'
  | 'reviews'
  | 'wallet';

const TOUR_STEPS: Array<{
  slug: GuideTourSlug;
  title: string;
  description: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
}> = [
  {
    slug: 'today',
    title: 'Today',
    description:
      "Your hosting home — see what's happening today and jump into the work that needs attention.",
    side: 'right',
  },
  {
    slug: 'bookings',
    title: 'Bookings',
    description: 'Review upcoming and active bookings, respond to travelers, and manage trip details.',
    side: 'right',
  },
  {
    slug: 'past-trips',
    title: 'Past trips',
    description: 'Browse completed trips for history, follow-ups, and past traveler context.',
    side: 'right',
  },
  {
    slug: 'pricing',
    title: 'Pricing',
    description: 'Set your hourly and full-day rates so travelers see clear pricing on your listing.',
    side: 'right',
  },
  {
    slug: 'calendar',
    title: 'Calendar',
    description: 'Mark when you are available or busy so bookings only land on days you can host.',
    side: 'right',
  },
  {
    slug: 'reviews',
    title: 'Reviews',
    description: 'Read traveler feedback and keep an eye on how your hosting is landing.',
    side: 'right',
  },
  {
    slug: 'wallet',
    title: 'Wallet',
    description:
      'Check your available balance, transactions, withdrawals, and bank accounts for payouts.',
    side: 'left',
  },
];

function storageKey(userId: string) {
  return `${STORAGE_KEYS.GUIDE_DASHBOARD_TOUR_SEEN}:${userId}`;
}

export function shouldForceGuideDashboardTour(): boolean {
  return import.meta.env.VITE_FORCE_GUIDE_DASHBOARD_TOUR === 'true';
}

export function hasSeenGuideDashboardTour(userId: string): boolean {
  try {
    return localStorage.getItem(storageKey(userId)) === '1';
  } catch {
    return false;
  }
}

export function markGuideDashboardTourSeen(userId: string): void {
  try {
    localStorage.setItem(storageKey(userId), '1');
  } catch {
    // Ignore quota / private mode failures — tour may show again next visit.
  }
}

function tourSelector(slug: GuideTourSlug, surface: 'desktop' | 'mobile'): string {
  return `[${GUIDE_TOUR_ATTR}="${slug}"][${GUIDE_TOUR_ATTR}-surface="${surface}"]`;
}

function buildSteps(isDesktop: boolean): DriveStep[] {
  const surface = isDesktop ? 'desktop' : 'mobile';

  return TOUR_STEPS.map((step) => ({
    element: tourSelector(step.slug, surface),
    popover: {
      title: step.title,
      description: step.description,
      side: isDesktop ? step.side : step.slug === 'wallet' ? 'bottom' : 'top',
      align: 'start',
    },
  }));
}

export type StartGuideDashboardTourOptions = {
  isDesktop: boolean;
  /** Called when the tour finishes, is skipped/closed, or is destroyed. */
  onDone: () => void;
};

export function startGuideDashboardTour({
  isDesktop,
  onDone,
}: StartGuideDashboardTourOptions): Driver {
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    onDone();
  };

  const instance = driver({
    showProgress: true,
    animate: true,
    allowClose: true,
    overlayOpacity: 0.55,
    stagePadding: 8,
    stageRadius: 10,
    popoverOffset: 12,
    popoverClass: 'guide-dashboard-tour-popover',
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Got it',
    progressText: '{{current}} of {{total}}',
    steps: buildSteps(isDesktop),
    skipMissingElement: true,
    // Prevent accidental close when the overlay receives a mis-aimed click.
    overlayClickBehavior: 'nextStep',
    disableActiveInteraction: true,
    onDestroyStarted: (_el, _step, { driver: d }) => {
      // Required override: without destroy() here, close is blocked.
      finish();
      d.destroy();
    },
    onDestroyed: () => {
      finish();
    },
  });

  instance.drive();
  return instance;
}
